/**
 * Vægtbaseret fragtberegning — spec §4.3.
 *
 * - Tiers sorteres stigende efter min_weight_kg; første tier hvor
 *   vægt ≥ min && (max er null || vægt < max) matcher.
 * - Matcher ingen tier og vægt ≥ sidste tiers min → sidste tiers pris; ellers 0.
 * - Afhentning (pickup) ⇒ fragt 0.
 * - FRI FRAGT: subtotal (ex moms) ≥ FREE_SHIPPING_THRESHOLD_EX_VAT ⇒ fragt 0
 *   (besluttet 2026-09; matcher løftet "Fri fragt over 1.500 kr" i headeren).
 *   Sælger-override slår fri fragt (override er et bevidst valg).
 * - Sælger-override (≥ 0, ex moms) erstatter den beregnede pris.
 * - Fragtpriser er EX moms.
 *
 * Ren logik — ingen React/Supabase/I/O.
 */

/** Række fra shipping_tiers (pris ex moms). */
export interface ShippingTier {
  minWeightKg: number;
  /** null = ingen øvre grænse. */
  maxWeightKg: number | null;
  /** Fragtpris ex moms. */
  price: number;
  name?: string | null;
}

export type DeliveryMethod = "shipping" | "pickup";

/** Fri fragt ved ordresubtotal (ex moms) på/over denne grænse. */
export const FREE_SHIPPING_THRESHOLD_EX_VAT = 1500;

export interface ResolveShippingOptions {
  deliveryMethod?: DeliveryMethod;
  /** Sælger-override (ex moms). Ignoreres hvis null/undefined eller negativ. */
  priceOverrideExVat?: number | null;
  /** Ordresubtotal ex moms (efter rabatter, før cashback) — udløser fri fragt. */
  subtotalExVat?: number;
  /** Grænse for fri fragt (ex moms). Default FREE_SHIPPING_THRESHOLD_EX_VAT. */
  freeShippingThresholdExVat?: number;
}

export interface ShippingResult {
  /** Fragtpris ex moms. */
  priceExVat: number;
  tierName: string | null;
  source: "pickup" | "override" | "free" | "tier" | "none";
}

/**
 * Manglende beløb (ex moms) før fri fragt. 0 når grænsen er nået.
 * Bruges til "Køb for X kr mere → fri fragt"-nudge i kurven.
 */
export function amountUntilFreeShipping(
  subtotalExVat: number,
  thresholdExVat: number = FREE_SHIPPING_THRESHOLD_EX_VAT,
): number {
  if (!Number.isFinite(subtotalExVat) || subtotalExVat < 0) return thresholdExVat;
  return Math.max(0, thresholdExVat - subtotalExVat);
}

/** Konverter vægt til kg ud fra Shopify-vægtenhed (spec §4.3). */
export function convertToKg(
  weight: number | null | undefined,
  unit: string | null | undefined,
): number {
  if (typeof weight !== "number" || !Number.isFinite(weight) || weight === 0) return 0;
  switch (unit?.toUpperCase()) {
    case "GRAMS":
      return weight / 1000;
    case "POUNDS":
      return weight * 0.453592;
    case "OUNCES":
      return weight * 0.0283495;
    case "KILOGRAMS":
    default:
      return weight;
  }
}

export interface WeighedLine {
  weight?: number | null;
  weightUnit?: string | null;
  quantity: number;
}

/** Samlet vægt i kg: Σ convertToKg(vægt, enhed) × antal. */
export function computeTotalWeightKg(items: readonly WeighedLine[]): number {
  return items.reduce((sum, item) => {
    const qty =
      typeof item.quantity === "number" && Number.isFinite(item.quantity) && item.quantity > 0
        ? item.quantity
        : 0;
    return sum + convertToKg(item.weight, item.weightUnit) * qty;
  }, 0);
}

/**
 * Opløs fragtpris (ex moms) for en samlet vægt i kg.
 * Rækkefølge: pickup ⇒ 0 → sælger-override → tier-opslag.
 */
export function resolveShipping(
  totalWeightKg: number,
  tiers: readonly ShippingTier[],
  opts: ResolveShippingOptions = {},
): ShippingResult {
  // Afhentning på lager ⇒ altid 0 (også ved override).
  if (opts.deliveryMethod === "pickup") {
    return { priceExVat: 0, tierName: null, source: "pickup" };
  }

  // Sælger-override (≥ 0) erstatter den beregnede pris — også fri fragt.
  const override = opts.priceOverrideExVat;
  if (typeof override === "number" && Number.isFinite(override) && override >= 0) {
    return { priceExVat: override, tierName: null, source: "override" };
  }

  // Fri fragt ved subtotal (ex moms) på/over grænsen.
  const threshold = opts.freeShippingThresholdExVat ?? FREE_SHIPPING_THRESHOLD_EX_VAT;
  if (
    typeof opts.subtotalExVat === "number" &&
    Number.isFinite(opts.subtotalExVat) &&
    threshold > 0 &&
    opts.subtotalExVat >= threshold
  ) {
    return { priceExVat: 0, tierName: null, source: "free" };
  }

  const weight =
    typeof totalWeightKg === "number" && Number.isFinite(totalWeightKg) && totalWeightKg > 0
      ? totalWeightKg
      : 0;

  if (tiers.length === 0) {
    return { priceExVat: 0, tierName: null, source: "none" };
  }

  const sorted = [...tiers].sort((a, b) => a.minWeightKg - b.minWeightKg);

  const match = sorted.find(
    (tier) =>
      weight >= tier.minWeightKg &&
      (tier.maxWeightKg === null || weight < tier.maxWeightKg),
  );
  if (match) {
    return { priceExVat: match.price, tierName: match.name ?? null, source: "tier" };
  }

  // Overstiger vægten alle tiers, bruges den højeste tier.
  const highest = sorted[sorted.length - 1];
  if (highest && weight >= highest.minWeightKg) {
    return { priceExVat: highest.price, tierName: highest.name ?? null, source: "tier" };
  }

  return { priceExVat: 0, tierName: null, source: "none" };
}
